import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';
import { getPrimaryDiscountCodeFromUrl } from '@theme/free-processing-discount';
import { formatMoney } from '@theme/money-formatting';

const SITEWIDE_DISCOUNT_PRICE_EXPOSURE_EVENT = 'legacybox_sitewide_discount_price_exposure';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #isProductCard = false;
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    if (this.dataset.productCard) this.#isProductCard = true;

    this.#refreshDiscountState();
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    document.addEventListener(ThemeEvents.discountUpdate, this.#refreshDiscountState, { signal });
    window.addEventListener('pageshow', this.#refreshDiscountState, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;

    this.#abortController.abort();
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');

    this.addDiscountPrice(newPrice);
    this.addFreeShippingBadge(newPrice);

    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.#refreshDiscountState();
    }
  };

  #refreshDiscountState = () => {
    const price = this.querySelector('[ref="priceContainer"]');

    this.addDiscountPrice(price);
    this.addFreeShippingBadge(price);
  };

  /**
   * Add/Remove discount price
   * @param {Element | null} element
   */
  addDiscountPrice(element) {
    if (!element) return;

    const price = Number(element.getAttribute('data-price'));
    const productId = element.getAttribute('data-product-id');
    const variantId = element.getAttribute('data-variant-id');

    if (this.#isProductCard && !this.#isAutoApplyAllowed(price)) {
      return;
    }

    const resolved = this.#resolveDiscount(variantId, productId);

    if (!resolved) {
      this.#removeDiscountElements(element);
      return;
    }

    const { discountedPrice, badgeText } = this.#computeDiscount(resolved, price);

    if (discountedPrice == null) {
      this.#removeDiscountElements(element);
      return;
    }

    const priceEl = /** @type {HTMLElement} */ (
      element.querySelector('.discount-price') ?? document.createElement('div')
    );
    priceEl.className = 'discount-price';
    priceEl.innerText = discountedPrice;
    element.append(priceEl);

    if (badgeText) {
      const badgeEl = /** @type {HTMLElement} */ (
        element.querySelector('.discount-badge') ?? document.createElement('div')
      );
      badgeEl.className = 'discount-badge';
      badgeEl.innerText = badgeText;
      element.append(badgeEl);
    }

    this.#trackSitewideDiscountPriceExposure(element, resolved, {
      badgeText,
      discountedPrice,
      price,
      productId,
      variantId,
    });
  }

  /**
   * Check is Auto Apply discount allowed for product card
   * @param {Number} price
   */
  #isAutoApplyAllowed(price) {
    const config = Theme.legacyBox.discounts;
    const isAllow = config.allow && config.sitewide.strikethrough;

    if (!isAllow) return false;

    if (config.sitewide.threshold.active) {
      return price > config.sitewide.threshold.amount;
    }

    return true;
  }

  /**
   * Resolve the best-matching discount for a variant/product. Cascade is:
   *   1. Explicit override mapped to this variant; BOGO overrides require an active/requested code
   *   2. Currently-applied cart discount, if it can render for this card
   *   3. Auto-apply fallback (default_discount), if it can render for this card
   *
   * @param {string | null} variantId
   * @param {string | null} productId
   * @returns {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any } | null}
   */
  #resolveDiscount(variantId, productId) {
    const override = variantId
      ? Theme.legacyBox.discounts.overrides.find((o) => {
          return o.variant_id.includes(`${variantId}`) && this.#isOverrideDiscountActive(o);
        })
      : null;
    if (override) return { source: 'override', override };

    const applied = this.#readSession(Theme.legacyBox.discounts.sessionKey);
    if (this.#isRenderableDiscount(applied, productId)) {
      return { source: 'applied', data: applied };
    }

    const fallback = this.#readSession(Theme.legacyBox.discounts.defaultSessionKey);
    if (this.#isRenderableDiscount(fallback, productId)) {
      return { source: 'fallback', data: fallback };
    }

    return null;
  }

  /**
   * @returns {string | null}
   */
  #getUrlDiscountCode() {
    return getPrimaryDiscountCodeFromUrl();
  }

  /**
   * @param {any} data
   * @returns {string | null}
   */
  #getSessionDiscountCode(data) {
    const code = data?.code;
    return typeof code === 'string' && code.trim() ? code.trim().toUpperCase() : null;
  }

  /**
   * @param {any} override
   */
  #isOverrideDiscountActive(override) {
    if (override?.additive) return false;
    if (!override?.is_bogo) return true;

    const overrideCode =
      typeof override?.auto_discount === 'string'
        ? override.auto_discount.trim().toUpperCase()
        : '';

    if (!overrideCode) return false;

    return [
      this.#getUrlDiscountCode(),
      this.#getSessionDiscountCode(this.#readSession(Theme.legacyBox.discounts.sessionKey)),
    ].includes(overrideCode);
  }

  /**
   * @param {string} key
   */
  #readSession(key) {
    if (!key) return null;
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }

  /**
   * @param {any} data
   * @param {string | null} productId
   */
  #isRenderableDiscount(data, productId) {
    return this.#productIsInScope(data, productId) && this.#hasDisplayableValue(data);
  }

  /**
   * @param {any} data
   */
  #hasDisplayableValue(data) {
    const value = data?.customerGets?.value;
    const percentage = Number(value?.percentage);
    const amount = Number(value?.amount?.amount);

    return Number.isFinite(percentage) || Number.isFinite(amount);
  }

  /**
   * DiscountCodeApp / Shopify Functions payloads have no customerGets — we
   * can't enumerate their scope client-side, so we assume eligibility.
   *
   * @param {any} data
   * @param {string | null} productId
   */
  #productIsInScope(data, productId) {
    if (!data || data.status === 'fail') return false;
    const items = data.customerGets?.items;
    if (!items) return true;
    if (items.allItems) return true;
    if (!productId) return false;
    return !!items.products?.nodes?.some(
      /** @param {{ id: string }} n */ (n) => n.id.includes(productId)
    );
  }

  /**
   * Compute price + badge text from the resolved discount source.
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any }} resolved
   * @param {number} price
   */
  #computeDiscount(resolved, price) {
    /** @type {any} */
    let valueData;
    /** @type {any} */
    let override;

    if (resolved.source === 'override') {
      override = resolved.override;
      valueData =
        override.type === 'percentage'
          ? { percentage: override.percentage }
          : { amount: { amount: override.amount_off } };
    } else {
      valueData = resolved.data.customerGets?.value;
    }

    if (!valueData) {
      return { discountedPrice: null, badgeText: null };
    }

    const isPercent = Boolean(valueData.percentage);
    const fixedAmount = Number(valueData.amount?.amount);
    const fixedAmountCents = Number.isFinite(fixedAmount) ? Math.round(fixedAmount * 100) : 0;
    const appliedDiscount = isPercent
      ? price * valueData.percentage
      : Math.min(price, fixedAmountCents);
    const rawDiscounted = price - appliedDiscount;

    let rounded;
    if (override?.price_type === 'manual' && override.price !== 0) {
      rounded = Math.max(0, override.price);
    } else {
      rounded = Number.isInteger(rawDiscounted) ? rawDiscounted : Math.ceil(rawDiscounted);
      rounded = Math.max(0, rounded);
    }

    const formattedPrice = formatMoney(rounded, Theme.format, Shopify.currency.active);

    const baseBadgeText = override?.is_bogo
      ? 'BOGO'
      : `Save ${
          isPercent
            ? this.#formatPercentage(valueData.percentage)
            : formatMoney(appliedDiscount, Theme.format, Shopify.currency.active)
        }`;
    const badgeCustomText = override?.badge_prefix?.trim();
    const badgeTextEnabled = override?.badge_text_enabled ?? Boolean(badgeCustomText);
    const badgeTextType = override?.badge_text_type ?? 'prefix';

    let badgeText = baseBadgeText;

    if (badgeTextEnabled && badgeCustomText) {
      badgeText =
        badgeTextType === 'suffix'
          ? `${baseBadgeText} ${badgeCustomText}`
          : `${badgeCustomText} ${baseBadgeText}`;
    }

    return { discountedPrice: formattedPrice, badgeText };
  }

  /**
   * @param {number | string} value
   * @returns {string}
   */
  #formatPercentage(value) {
    const percentage = Number(value) * 100;
    if (!Number.isFinite(percentage)) return '0%';

    return `${Math.round(percentage)}%`;
  }

  /**
   * @returns {Set<string>}
   */
  #getSitewideDiscountExposureRegistry() {
    window.Legacybox = window.Legacybox || {};
    window.Legacybox.sitewideDiscountPriceExposureKeys =
      window.Legacybox.sitewideDiscountPriceExposureKeys || new Set();

    return window.Legacybox.sitewideDiscountPriceExposureKeys;
  }

  /**
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any }} resolved
   */
  #getSitewideDiscountTestState(resolved) {
    if (resolved.source === 'override') return null;

    const sessionState = resolved.data?.sitewideDiscountAbTest;
    const liveState = window.Legacybox?.sitewideDiscountAbTest;
    const isVerifiedState = (state) => {
      return Boolean(
        state?.targetTestId &&
        state?.assignedHypothesisId &&
        state.source !== 'ineligible' &&
        state.eligible !== false
      );
    };
    const isVerifiedSessionState = (state) => {
      return isVerifiedState(state) && state.assignmentVerified === true;
    };

    if (isVerifiedSessionState(sessionState)) {
      if (
        isVerifiedState(liveState) &&
        liveState.targetTestId === sessionState.targetTestId &&
        liveState.assignedHypothesisId === sessionState.assignedHypothesisId
      ) {
        return liveState;
      }

      return sessionState;
    }

    if (isVerifiedState(liveState) && liveState.targetTestId === sessionState?.targetTestId) {
      return liveState;
    }

    return null;
  }

  /**
   * @param {Element} element
   * @param {{ source: 'override', override: any } | { source: 'applied' | 'fallback', data: any }} resolved
   * @param {{ badgeText: string | null, discountedPrice: string, price: number, productId: string | null, variantId: string | null }} exposure
   */
  #trackSitewideDiscountPriceExposure(element, resolved, exposure) {
    const state = this.#getSitewideDiscountTestState(resolved);
    const discountCode =
      resolved.source === 'override' ? null : this.#getSessionDiscountCode(resolved.data);

    if (!state?.targetTestId || !state?.bucket || !discountCode) return;
    if (state.source === 'ineligible' || state.eligible === false || !state.assignedHypothesisId) {
      return;
    }

    const key = [
      state.targetTestId,
      state.bucket,
      discountCode,
      exposure.productId || '',
      exposure.variantId || '',
    ].join(':');
    const registry = this.#getSitewideDiscountExposureRegistry();

    if (registry.has(key)) return;

    if (element.getAttribute('data-sitewide-discount-price-exposure-key') === key) return;

    element.setAttribute('data-sitewide-discount-price-exposure-key', key);

    const publishExposure = () => {
      if (registry.has(key)) return;

      registry.add(key);
      element.removeAttribute('data-sitewide-discount-price-exposure-key');

      const payload = {
        event: SITEWIDE_DISCOUNT_PRICE_EXPOSURE_EVENT,
        test_name: state.testName || 'sitewide-discount-code-v1',
        test_id: state.targetTestId,
        bucket: state.bucket,
        discount_code: discountCode,
        assigned_hypothesis_id: state.assignedHypothesisId || null,
        assignment_source: state.source || null,
        assignment_reason: state.reason || null,
        price_source: resolved.source,
        product_id: exposure.productId,
        variant_id: exposure.variantId,
        price_cents: exposure.price,
        discounted_price: exposure.discountedPrice,
        badge_text: exposure.badgeText,
        page_path: window.location.pathname,
      };

      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push(payload);
      }

      document.dispatchEvent(
        new CustomEvent('legacybox:sitewide-discount-price-exposure', {
          detail: payload,
        })
      );
    };

    if (!('IntersectionObserver' in window)) {
      publishExposure();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;

        observer.disconnect();
        publishExposure();
      },
      { threshold: 0.1 }
    );

    observer.observe(element);
  }

  /**
   * Remove discount price
   * @param {Element} element
   */
  #removeDiscountElements(element) {
    element.querySelector('.discount-price')?.remove();
    element.querySelector('.discount-badge')?.remove();
  }

  /**
   * Add/Remove free shipping badge
   * @param {Element | null} element
   */
  addFreeShippingBadge(element) {
    if (!element) return;

    const storage = sessionStorage.getItem(Theme.legacyBox.discounts.sessionKey);

    if (!storage) {
      this.#removeFreeShippingBadge(element);
      return;
    }

    const discountsData = JSON.parse(storage);
    const variantId = element.getAttribute('data-variant-id');
    const freeShipping = Theme.legacyBox.discounts.free_shippings.find(
      (f) => f.discount === discountsData.code && f.variant_id === variantId
    );

    if (freeShipping) {
      const freeShippingEl =
        element.querySelector('.free-shipping-badge') ?? document.createElement('div');
      freeShippingEl.classList = 'free-shipping-badge';
      freeShippingEl.innerText = freeShipping.text;

      element.append(freeShippingEl);
      return;
    }

    this.#removeFreeShippingBadge(element);
  }

  /**
   * Remove discount price
   * @param {Element} element
   */
  #removeFreeShippingBadge(element) {
    element.querySelector('.free-shipping-badge')?.remove();
  }
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
